import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BubbleMenu as EditorBubbleMenu, Editor, EditorContent, useEditor, Extension } from '@tiptap/react';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import Document from '@tiptap/extension-document';
import ListItem from '@tiptap/extension-list-item';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Heading, { Level } from '@tiptap/extension-heading';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
// import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import History from '@tiptap/extension-history';
import { Plugin } from '@tiptap/pm/state';
import {
  BoldOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  UnderlineOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { ImClearFormatting } from 'react-icons/im';
import { css } from '@emotion/react';
import { Button, Divider, Input, Select } from 'antd';
import { InfoCircleOutlined, WarningOutlined } from 'antd/icons';
import { SessionForm } from './session-form';

const CONTENT_SIZE_LIMIT = 7900;
const PRE_CONTENT_SIZE_LIMIT = Math.floor(CONTENT_SIZE_LIMIT * 0.95);

export const ContentLimitExtension = Extension.create({
  name: 'contentLimit',

  addOptions() {
    return {
      limit: 4000
    };
  },

  addStorage() {
    return {
      current: 0,
      limit: this.options.limit
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        view: () => ({
          update: () => {
            this.storage.current = this.editor.getHTML().length;
          }
        })
      })
    ];
  }
});

const MenuBar = ({
  readOnly,
  editor
  // openBubble
}: {
  readOnly?: boolean;
  editor: Editor | null;
  // openBubble: () => void;
}) => {
  const { t } = useTranslation('common');
  const [heading, setHeading] = useState(0);

  const headingOptions = [
    { value: 1, label: <h1 style={{ margin: '0px', padding: '8px' }}>{t('Heading 1')}</h1>, title: 'Heading 1' },
    { value: 2, label: <h2 style={{ margin: '0px', padding: '8px' }}>{t('Heading 2')}</h2>, title: 'Heading 2' },
    { value: 3, label: <h3 style={{ margin: '0px', padding: '8px' }}>{t('Heading 3')}</h3>, title: 'Heading 3' },
    { value: 0, label: 'Normal', title: 'Normal' }
  ];

  const currentTextIsHeading = editor?.isActive('heading');
  const currentTextHeadingLevel = editor?.getAttributes('heading')?.level;

  useEffect(() => {
    if (currentTextIsHeading) {
      setHeading(currentTextHeadingLevel);
    } else {
      setHeading(0);
    }
  }, [currentTextIsHeading, currentTextHeadingLevel]);

  const onChangeHeading = (selected: number) => {
    if (readOnly) return;
    setHeading(selected);
    if (selected === 0) {
      // This removes any heading formatting applied
      // Making it a paragraph
      const selectedLevel = editor?.getAttributes('heading').level;
      editor?.chain().focus().toggleHeading({ level: selectedLevel }).run();
    }
    editor
      ?.chain()
      .focus()
      .setHeading({ level: selected as Level })
      .run();
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="control-group">
      <div className="button-group">
        <Select
          data-testid="heading-select"
          options={headingOptions}
          optionLabelProp="title"
          onChange={onChangeHeading}
          value={heading}
          className="format-select"
          popupMatchSelectWidth={false}
          variant="borderless"
          disabled={readOnly}
        />
        <BoldOutlined
          data-testid="bold-button"
          onClick={() => !readOnly && editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run() || readOnly}
          className={editor.isActive('bold') ? 'is-active icon' : 'icon'}
        />
        <ItalicOutlined
          data-testid="italic-button"
          onClick={() => !readOnly && editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run() || readOnly}
          className={editor.isActive('italic') ? 'is-active icon' : 'icon'}
        />
        <UnderlineOutlined
          data-testid="underlined-button"
          onClick={() => !readOnly && editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run() || readOnly}
          className={editor.isActive('underline') ? 'is-active icon' : 'icon'}
        />
        {/* <LinkOutlined
          data-testid="link-button"
          onClick={openBubble}
          className={editor.isActive('link') ? 'is-active icon' : 'icon'}
          disabled={readOnly}
        /> */}
        <span className="divider" />
        <UnorderedListOutlined
          data-testid="unordered-list-button"
          onClick={() => !readOnly && editor.chain().focus().toggleBulletList().run()}
          disabled={readOnly}
          className={editor.isActive('bulletList') ? 'is-active icon' : 'icon'}
        />
        <OrderedListOutlined
          data-testid="ordered-list-button"
          onClick={() => !readOnly && editor.chain().focus().toggleOrderedList().run()}
          disabled={readOnly}
          className={editor.isActive('orderedList') ? 'is-active icon' : 'icon'}
        />
        <span className="divider" />
        <ImClearFormatting
          data-testid="clear-button"
          style={{ cursor: 'pointer' }}
          onClick={() => !readOnly && editor.chain().focus().clearNodes().unsetAllMarks().run()}
        />
      </div>
    </div>
  );
};

const ContentPercentageUsed = ({ limit, editor }: { limit: number; editor: Editor | null }) => {
  if (!editor) return null;

  const current = editor.storage.contentLimit.current;
  const percentage = Math.min(Math.floor((current / limit) * 100), 100);

  const isLimitReached = current >= limit;

  return (
    <div className={`character-count ${isLimitReached ? 'character-count--warning' : ''}`}>
      <svg height="20" width="20" viewBox="0 0 20 20">
        <circle r="10" cx="10" cy="10" fill="#e9ecef" />
        <circle
          r="5"
          cx="10"
          cy="10"
          fill="transparent"
          stroke="currentColor"
          strokeWidth="10"
          strokeDasharray={`${(percentage / 100) * 31.4} 31.4`}
          transform="rotate(-90) translate(-20)"
        />
        <circle r="6" cx="10" cy="10" fill="white" />
      </svg>

      <span>{percentage}%</span>
    </div>
  );
};

const NoteLimitWarning = ({ warningLevel }: { warningLevel: number }) => {
  const config = {
    0: null,
    1: {
      color: '#faad14',
      icon: <InfoCircleOutlined />,
      text: 'Approaching maximum size limit'
    },
    2: {
      color: '#ff0000',
      icon: <WarningOutlined />,
      text: 'Reached maximum size limit'
    }
  }[warningLevel];

  if (!config) return null;

  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        height: 16px;
        margin-top: 4px;
        font-size: 10px;
        line-height: 12px;
        color: ${config.color};

        .note-limit {
          margin-left: auto;
        }
      `}
    >
      <span>
        {config.icon} {config.text}
      </span>
    </div>
  );
};

const getExtensions = (placeholderText?: string, limit?: number, readOnly = false) => [
  BulletList,
  OrderedList,
  Document,
  ListItem,
  Paragraph,
  Text,
  Bold,
  Italic,
  // Link.configure({
  //   autolink: false,
  //   linkOnPaste: false,
  //   openOnClick: readOnly
  // }),
  Underline,
  Heading.configure({
    levels: [1, 2, 3]
  }),
  Placeholder.configure({ placeholder: placeholderText || '' }),
  ContentLimitExtension.configure({ limit }),
  BubbleMenu,
  History
];

// Not used currently but this is the menu for adding/editing/removing links
// All code left in but commented out
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LinkMenu = ({
  editor,
  readOnly,
  bubbleMode,
  setBubbleMode,
  bubbleEditValue,
  setBubbleEditValue
}: {
  editor: Editor;
  readOnly: boolean;
  bubbleMode: 'view' | 'edit';
  setBubbleMode: Dispatch<SetStateAction<'view' | 'edit'>>;
  bubbleEditValue: string;
  setBubbleEditValue: Dispatch<SetStateAction<string>>;
}) => {
  const { t } = useTranslation('common');
  return (
    <EditorBubbleMenu
      shouldShow={() => {
        return editor.isActive('link') && !readOnly;
      }}
      editor={editor}
      tippyOptions={{
        onHidden: () => {
          setBubbleMode('view');
        },
        onShow: () => {
          if (editor?.getAttributes('link').href === '') {
            setBubbleMode('edit');
            setBubbleEditValue('');
          } else {
            setBubbleMode('view');
            setBubbleEditValue(editor?.getAttributes('link').href);
          }
        }
      }}
    >
      <div className="bubble-menu">
        {bubbleMode === 'view' && (
          <>
            <span>
              {t('Visit URL:')}
              {'  '}
              <a target="_blank" rel="noopener noreferrer nofollow" href={editor?.getAttributes('link').href}>
                {editor?.getAttributes('link').href}
              </a>
            </span>
            <span>
              <Button
                type="link"
                data-testid="edit-url-button"
                onClick={() => {
                  setBubbleMode('edit');
                  setBubbleEditValue(editor?.getAttributes('link').href);
                }}
              >
                {t('Edit')}
              </Button>
              <Divider type="vertical" />
              <Button
                type="link"
                data-testid="remove-url-button"
                onClick={() => {
                  editor?.chain().focus().extendMarkRange('link').unsetLink().run();
                }}
              >
                {t('Remove')}
              </Button>
            </span>
          </>
        )}
        {bubbleMode === 'edit' && (
          <>
            {t('Enter link:')}{' '}
            <Input size="small" value={bubbleEditValue} onChange={(e) => setBubbleEditValue(e.target.value)} />
            <Button
              type="link"
              data-testid="save-url-button"
              onClick={() => {
                if (bubbleEditValue === '') {
                  // Remove link if all text deleted
                  editor?.chain().focus().extendMarkRange('link').unsetLink().run();
                } else {
                  editor?.chain().focus().extendMarkRange('link').setLink({ href: bubbleEditValue }).run();
                  setBubbleMode('view');
                  setBubbleEditValue('');
                }
              }}
            >
              {t('Save')}
            </Button>
          </>
        )}
      </div>
    </EditorBubbleMenu>
  );
};

const WrappedTipTapEditor = ({
  id,
  onChange,
  value = '',
  placeholder,
  readOnly,
  ariaLabel
}: {
  id: keyof SessionForm;
  onChange: ({
    field,
    value,
    skipSanitisation
  }: {
    field: keyof SessionForm;
    value: string;
    skipSanitisation?: boolean;
  }) => void;
  value?: string;
  placeholder?: string;
  readOnly: boolean;
  ariaLabel?: string;
}) => {
  const singleLinePlaceholder = placeholder ? placeholder.replace(/[\r\n]+/g, ' ') : '';
  const accessibilityLabel = ariaLabel ? `${ariaLabel}. ${singleLinePlaceholder}` : singleLinePlaceholder;

  const extensions = getExtensions(placeholder, 4000, readOnly);
  // const [bubbleMode, setBubbleMode] = useState<'edit' | 'view'>('view');
  // const [bubbleEditValue, setBubbleEditValue] = useState('');
  const editor = useEditor({
    content: value,
    extensions,
    onUpdate: ({ editor }) => {
      if (readOnly) return;
      isInternalUpdate.current = true;
      onChange({ field: id, value: editor.getHTML(), skipSanitisation: true });
    },
    editable: !readOnly,
    // onSelectionUpdate: () => {
    //   setBubbleMode('view');
    //   setBubbleEditValue('');
    // }
    // other editor config
    editorProps: {
      attributes: {
        'data-testid': `${id.toLowerCase()}-editor`,
        ...(accessibilityLabel ? { 'aria-label': accessibilityLabel } : {})
      }
    }
  });

  // Sync external value changes into TipTap (e.g. AI-generated notes)
  const isInternalUpdate = useRef(false);
  useEffect(() => {
    if (editor && value && !isInternalUpdate.current && editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
    isInternalUpdate.current = false;
  }, [editor, value]);

  // Dynamically set the editor to be editable. Setting editable in useEditor is a one-time call and does not update
  // until the page reloads / component re-renders
  useEffect(() => {
    if (!editor?.isDestroyed) {
      editor?.setEditable(!readOnly, false);
    }
  }, [editor, readOnly]);

  const contentSize = editor?.storage.contentLimit.current;

  const noteSizeWarning = useMemo(() => {
    if (contentSize < PRE_CONTENT_SIZE_LIMIT) {
      return 0;
    }
    if (contentSize >= PRE_CONTENT_SIZE_LIMIT && contentSize < CONTENT_SIZE_LIMIT) {
      return 1;
    }
    if (contentSize >= CONTENT_SIZE_LIMIT) {
      return 2;
    }
    return 0;
  }, [contentSize]);

  return (
    <>
      <div
        css={css`
          border: 1px solid #ccc;
          max-width: 100%;
          background-color: ${readOnly ? '#f5f5f5' : 'transparent'};

          .tiptap {
            padding: 1em 0;
            height: 160px;
            overflow-y: auto;

            p,
            h1,
            h2,
            h3 {
              line-height: 1.42;
              tab-size: 4;
              white-space: pre-wrap;
              word-wrap: break-word;
              font-family: Helvetica, Arial, sans-serif;
              margin: 0 8px;
              padding: 0;
              font-weight: 500;
            }

            p {
              font-size: 13px;
            }

            h1 {
              font-size: 2em;
            }

            h2 {
              font-size: 1.5em;
            }

            h3 {
              font-size: 1.17em;
            }
          }

          .button-group {
            display: flex;
            align-items: center;
            border-bottom: 1px solid #ccc;
            padding: 4px 10px;

            .format-select {
              width: 120px;
            }

            .is-active {
              color: blue;
            }

            .icon {
              font-size: 16px;
              margin: 0px 6px;
            }

            .divider {
              margin: 0px 8px;
            }
          }

          .slot-after {
            display: flex;
            justify-content: flex-end;
            padding: 1px;
            border-top: 1px solid #ccc;
          }

          .character-count {
            display: flex;
            align-items: center;
            margin: 2px;

            span {
              margin-left: 4px;
            }
          }

          .character-count--warning {
            color: #ff0000;
          }

          .bubble-menu {
            display: flex;
            align-items: center;
            gap: 16px;
            border-radius: 2px;
            background-color: #fff;
            border: 1px solid #ccc;
            box-shadow: 0 0 5px #ddd;
            color: #444;
            padding: 5px 12px;
            white-space: nowrap;
            font-size: 13px;
            line-height: 1.5715;
          }

          p.is-editor-empty:first-child::before {
            content: attr(data-placeholder); /* Fallback for older browsers */
            content: attr(data-placeholder) / '';
            position: absolute;
            left: 5;
            top: 5;
            pointer-events: none;
            font-size: 12px;
            font-style: italic;
            line-height: 18px;
            color: var(--black);
            opacity: 0.3;
          }
        `}
      >
        <MenuBar
          readOnly={readOnly}
          editor={editor}
          // openBubble={() => {
          //   editor?.chain().focus().extendMarkRange('link').setLink({ href: '' }).run();
          //   // setBubbleMode('edit');
          // }}
        />
        {/* {editor && !readOnly && (
        <LinkMenu
          editor={editor}
          readOnly={readOnly}
          bubbleMode={bubbleMode}
          setBubbleMode={setBubbleMode}
          bubbleEditValue={bubbleEditValue}
          setBubbleEditValue={setBubbleEditValue}
        />
      )} */}
        <EditorContent editor={editor} />
        <div className="slot-after">
          <ContentPercentageUsed limit={CONTENT_SIZE_LIMIT} editor={editor} />
        </div>
      </div>
      <NoteLimitWarning warningLevel={noteSizeWarning} />
    </>
  );
};

export default WrappedTipTapEditor;
